"use client"

import { useState, useEffect } from "react"
import { Navigate } from "react-router-dom"
import { useAuth } from "../../../contexts/AuthContext"
import Swal from "sweetalert2"
import {
  MagnifyingGlassIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  CogIcon,
  ArrowLeftIcon,
  ShieldCheckIcon,
  BellIcon,
  BuildingOfficeIcon,
  CheckBadgeIcon,
  SignalIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline"

export default function Wabadash() {
  const { user, getAllWabaAccounts, addWabaById, updateUserCredentials, getPhoneNumberDetails, makeDefaultPhone, syncUserLimit } =
    useAuth()

  const [wabaAccounts, setWabaAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [syncingLimit, setSyncingLimit] = useState(false)

  const handleSyncWabaLimit = async () => {
    if (!user?._id) return
    try {
      setSyncingLimit(true)
      const res = await syncUserLimit(user._id)
      if (res && res.success) {
        Swal.fire({
          icon: "success",
          title: "Limit Synced",
          text: `Messaging limit synced from Meta: ${res.wabaLimit || user?.wabaLimit || "10K"}`,
          timer: 1800,
          showConfirmButton: false,
          confirmButtonColor: "#4A7C59",
        })
      } else {
        Swal.fire({
          icon: "error",
          title: "Sync Failed",
          text: res?.error || res?.message || "Failed to sync limit from Meta API",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      console.error("Wabadash sync limit error:", err)
    } finally {
      setSyncingLimit(false)
    }
  }
  const [searchTerm, setSearchTerm] = useState("")
  const [summary, setSummary] = useState({})
  const [showAddWaba, setShowAddWaba] = useState(false)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [newWabaId, setNewWabaId] = useState("")
  const [addingWaba, setAddingWaba] = useState(false)
  const [error, setError] = useState("")

  const [selectedAccount, setSelectedAccount] = useState(null)
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null)
  const [phoneNumberDetails, setPhoneNumberDetails] = useState(null)
  const [loadingPhoneDetails, setLoadingPhoneDetails] = useState(false)

  const [credentials, setCredentials] = useState({
    wabaId: "",
    phoneNumberId: "",
    businessAccountId: "",
    token: "",
  })
  const [savingCredentials, setSavingCredentials] = useState(false)

  useEffect(() => {
    if (user) {
      fetchWabaAccounts()
      loadExistingCredentials()
    }
  }, [user])

  if (user?.role === "reseller") {
    return <Navigate to="/dashreseller" replace />
  }
  if (user?.role === "admin") {
    return <Navigate to="/dash-admin" replace />
  }
  if (user?.role === "superadmin") {
    return <Navigate to="/su-admin" replace />
  }

  const loadExistingCredentials = () => {
    if (user) {
      console.log("[v0] Loading existing credentials from user:", user)
      const tokenValue = user.token || user.wptoken
      setCredentials({
        wabaId: user.wabaId || "",
        phoneNumberId: user.phoneNumberId || "",
        businessAccountId: user.businessAccountId || "",
        token: tokenValue ? "***configured***" : "",
      })
    }
  }

  const handleMakeDefaultPhone = async (wabaId, phoneId) => {
    try {
      setLoading(true)
      const result = await makeDefaultPhone(wabaId, phoneId)

      if (!result.success) {
        if (result.status === 401 || result.status === 403) {
          Swal.fire({
            icon: "warning",
            title: "Access denied",
            text: "Your session expired or you don't have permission.",
          })
          return
        }

        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.error || "Failed to set default",
        })
        return
      }

      Swal.fire({
        icon: "success",
        title: "Default set",
        text: "Default phone updated for this WABA",
        timer: 1400,
        showConfirmButton: false,
      })

      await fetchWabaAccounts() // refresh list
    } catch (err) {
      console.error(err)
      Swal.fire({ icon: "error", title: "Error", text: "Something went wrong" })
    } finally {
      setLoading(false)
    }
  }

  const pickDefaultFromResult = (accountsResult) => {
    if (!accountsResult || !accountsResult.accounts) return

    const accounts = accountsResult.accounts || []
    // prefer user.wabaId (configured in user) as default account
    let defaultAccount = null
    if (user?.wabaId) {
      defaultAccount = accounts.find((a) => (a.id || a.wabaId) === user.wabaId)
    }
    // fallback to first account
    if (!defaultAccount && accounts.length > 0) defaultAccount = accounts[0]

    if (defaultAccount) {
      // prefer configured phoneNumberId, else take first phone
      const defaultPhoneId =
        user?.phoneNumberId || defaultAccount.phoneNumbers?.[0]?.id || defaultAccount.phoneNumbers?.[0]?.phoneNumberId
      let phone =
        (defaultAccount.phoneNumbers || []).find(
          (p) => p.id === defaultPhoneId || p.phoneNumberId === defaultPhoneId,
        ) ||
        (defaultAccount.phoneNumbers || [])[0] ||
        null

      if (phone && !phone.id && phone.phoneNumberId) {
        phone = {
          ...phone,
          id: phone.phoneNumberId,
          display_phone_number:
            phone.displayPhoneNumber || phone.display_phone_number || phone.wanumber || phone.displayPhoneNumber,
        }
      }

      // Do not auto-select account on load/refresh so user stays on main accounts dashboard
      // setSelectedAccount(defaultAccount)
      // setSelectedPhoneNumber(phone)
      // if (phone?.id) {
      //   handlePhoneNumberClick(defaultAccount, phone)
      // }
    }
  }

  // Manual Sync button handler — re-fetch accounts and show feedback
  const handleSync = async () => {
    try {
      setLoading(true)
      const result = await fetchWabaAccounts(true) // pass true to force sync with database
      if (result !== false) {
        Swal.fire({
          icon: "success",
          title: "Synced",
          text: "WABA accounts synced with database successfully.",
          confirmButtonColor: "#4A7C59",
          timer: 1500,
          showConfirmButton: false,
        })
      }
    } catch (err) {
      console.error("Sync error:", err)
      Swal.fire({
        icon: "error",
        title: "Sync error",
        text: "Failed to sync WABA accounts. Check console for details.",
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setLoading(false)
    }
  }

  const getWhatsAppConfigurationStatus = () => {
    if (!user) return { configured: false, message: "User not loaded", details: "" }

    const hasWabaId = user.wabaId && user.wabaId.trim() !== ""
    const hasToken = (user.token && user.token.trim() !== "") || (user.wptoken && user.wptoken.trim() !== "")
    const hasPhoneNumberId = user.phoneNumberId && user.phoneNumberId.trim() !== ""
    const hasBusinessAccountId = user.businessAccountId && user.businessAccountId.trim() !== ""

    const operatorInfo = user.operator ? ` (${user.operator})` : ""

    // Full configuration requires WABA ID and Token at minimum
    const isFullyConfigured = hasWabaId && hasToken

    if (isFullyConfigured) {
      return {
        configured: true,
        message: `✓ WhatsApp configured successfully`
      }
    }

    // Partial configuration - show what's available
    if (hasWabaId && !hasToken) {
      return {
        configured: false,
        message: `⚠ WABA ID configured, Access Token missing${operatorInfo}`,
        details: `WABA ID: ${user.wabaId} (Token required for full access)`,
      }
    }

    if (hasToken && !hasWabaId) {
      return {
        configured: false,
        message: `⚠ Access Token configured, WABA ID missing${operatorInfo}`,
        details: "WABA ID required for WhatsApp Business API access",
      }
    }

    if (hasPhoneNumberId || hasBusinessAccountId) {
      const parts = []
      if (hasPhoneNumberId) parts.push(`Phone: ${user.phoneNumberId}`)
      if (hasBusinessAccountId) parts.push(`Business: ${user.businessAccountId}`)

      return {
        configured: false,
        message: `⚠ Partial configuration detected${operatorInfo}`,
        details: `${parts.join(" | ")} (WABA ID and Token required)`,
      }
    }

    return {
      configured: false,
      message: `✗ WhatsApp account not configured${operatorInfo}`,
      details: "WABA ID and Access Token required",
    }
  }

  const isWhatsAppConfigured = () => {
    return getWhatsAppConfigurationStatus().configured
  }

  const handleConfigureCredentials = async () => {
    // Check if at least one credential field is provided
    if (!credentials.wabaId && !credentials.phoneNumberId && !credentials.businessAccountId && !credentials.token) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please fill in at least one credential field",
        confirmButtonColor: "#4A7C59",
      })
      return
    }

    try {
      setSavingCredentials(true)

      const updateData = {}

      if (credentials.wabaId && credentials.wabaId !== user?.wabaId) {
        updateData.wabaId = credentials.wabaId
      }

      if (credentials.phoneNumberId && credentials.phoneNumberId !== user?.phoneNumberId) {
        updateData.phoneNumberId = credentials.phoneNumberId
      }

      if (credentials.businessAccountId && credentials.businessAccountId !== user?.businessAccountId) {
        updateData.businessAccountId = credentials.businessAccountId
      }

      if (credentials.token && credentials.token !== "***configured***") {
        updateData.token = credentials.token
      }

      // If no changes detected, show message
      if (Object.keys(updateData).length === 0) {
        Swal.fire({
          icon: "info",
          title: "No Changes",
          text: "No changes detected in credentials",
          confirmButtonColor: "#4A7C59",
        })
        setSavingCredentials(false)
        return
      }

      console.log("[v0] Updating credentials with:", updateData)

      const result = await updateUserCredentials(updateData)

      if (result.success) {
        setShowConfigModal(false)

        loadExistingCredentials()

        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "WhatsApp credentials updated successfully!",
          confirmButtonColor: "#4A7C59",
        })

        // Refresh the accounts to reflect new configuration
        fetchWabaAccounts()
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.error || "Failed to update credentials",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (error) {
      console.error("[v0] Error updating credentials:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update credentials. Please try again.",
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setSavingCredentials(false)
    }
  }

  const handlePhoneNumberClick = async (account, phoneNumber) => {
    setSelectedAccount(account)
    setSelectedPhoneNumber(phoneNumber)
    setLoadingPhoneDetails(true)

    try {
      console.log("[v0] Fetching phone number details for:", phoneNumber.id)
      const result = await getPhoneNumberDetails(phoneNumber.id)
      if (result.success) {
        setPhoneNumberDetails({
          ...result.phoneDetails,
          // Ensure we have all required fields with fallbacks
          display_phone_number: result.phoneDetails.display_phone_number || phoneNumber.display_phone_number,
          verified_name: result.phoneDetails.verified_name || phoneNumber.verified_name || "Infinity Solution Team",
          quality_rating: result.phoneDetails.quality_rating || phoneNumber.quality_rating || "GREEN",
          status: result.phoneDetails.status || phoneNumber.status || "CONNECTED",
          messaging_limit_tier:
            result.phoneDetails.messaging_limit_tier || phoneNumber.messaging_limit_tier || "TIER_1K",
          code_verification_status: result.phoneDetails.code_verification_status || "EXPIRED",
          name_status: result.phoneDetails.name_status || "APPROVED",
          is_official_business_account: result.phoneDetails.is_official_business_account || false,
          country: result.phoneDetails.country || { code: "IN", name: "India", flag: "🇮🇳" },
          throughput: result.phoneDetails.throughput || { level: "STANDARD" },
        })
      } else {
        console.error("Failed to fetch phone details:", result.error)
        setPhoneNumberDetails({
          ...phoneNumber,
          display_phone_number: phoneNumber.display_phone_number || "+91 82388 02323",
          verified_name: phoneNumber.verified_name || "Infinity Solution Team",
          quality_rating: phoneNumber.quality_rating || "GREEN",
          status: phoneNumber.status || "CONNECTED",
          messaging_limit_tier: phoneNumber.messaging_limit_tier || "TIER_1K",
          code_verification_status: "EXPIRED",
          name_status: "APPROVED",
          is_official_business_account: false,
          country: { code: "IN", name: "India", flag: "🇮🇳" },
          throughput: { level: "STANDARD" },
        })
      }
    } catch (error) {
      console.error("Error fetching phone details:", error)
      setPhoneNumberDetails({
        ...phoneNumber,
        display_phone_number: phoneNumber.display_phone_number || "+91 82388 02323",
        verified_name: phoneNumber.verified_name || "Infinity Solution Team",
        quality_rating: phoneNumber.quality_rating || "GREEN",
        status: phoneNumber.status || "CONNECTED",
        messaging_limit_tier: phoneNumber.messaging_limit_tier || "TIER_1K",
        code_verification_status: "EXPIRED",
        name_status: "APPROVED",
        is_official_business_account: false,
        country: { code: "IN", name: "India", flag: "🇮🇳" },
        throughput: { level: "STANDARD" },
      })
    } finally {
      setLoadingPhoneDetails(false)
    }
  }

  const handleBackToAccounts = () => {
    setSelectedAccount(null)
    setSelectedPhoneNumber(null)
    setPhoneNumberDetails(null)
  }

  // helper: format a phone number display value from account phone objects
  const findPhoneDisplayById = (account, phoneId) => {
    if (!account || !account.phoneNumbers || !phoneId) return null
    const phone = (account.phoneNumbers || []).find((p) => p.id === phoneId || p.phoneNumberId === phoneId)
    if (!phone) return null
    // support different property names returned by various sources
    return (
      phone.display_phone_number ||
      phone.displayPhoneNumber ||
      phone.displayPhoneNumber ||
      phone.wanumber ||
      phone.display_phone_number
    )
  }

  /* ===== Replace your fetchWabaAccounts with this version ===== */
  const fetchWabaAccounts = async (sync = false) => {
    try {
      setLoading(true)
      setError("")

      const result = await getAllWabaAccounts(sync)
      console.log("[v0] WABA accounts result:", result)

      if (result.success) {
        // Normalize accounts to expected keys (id, phoneNumbers[], default ...)
        const normalized = (result.accounts || []).map((acc) => {
          const phones = (acc.phoneNumbers || []).map((p) => ({
            id: p.id || p.phoneNumberId || p.phone_number_id,
            display_phone_number: p.display_phone_number || p.displayPhoneNumber || p.displayPhoneNumber || p.wanumber,
            verified_name: p.verified_name || p.verifiedName || null,
            quality_rating: p.quality_rating || p.qualityRating || null,
            status: p.status || null,
            messaging_limit_tier: p.messaging_limit_tier || p.messagingLimitTier || null,
            default: p.default || false,
          }))
          const defaultId = acc.defaultPhoneNumberId || acc.default_phone_number_id || null
          const defaultDisplay = defaultId
            ? phones.find((ph) => ph.id === defaultId)?.display_phone_number || null
            : null
          return {
            ...acc,
            phoneNumbers: phones,
            defaultPhoneNumberId: defaultId,
            defaultPhoneNumberDisplay: defaultDisplay,
          }
        })

        // Build summary from returned summary but prefer readable default from API
        const newSummary = { ...(result.summary || {}) }
        if (!newSummary.defaultPhoneNumber && result.summary?.defaultPhoneNumber) {
          newSummary.defaultPhoneNumber = result.summary.defaultPhoneNumber
          newSummary.defaultPhoneNumberId = result.summary.defaultPhoneNumberId || null
        }

        // If backend didn't provide a default, try to find from normalized accounts or user config
        if (!newSummary.defaultPhoneNumber) {
          let found = null
          for (const acc of normalized) {
            if (acc.defaultPhoneNumberId) {
              found = {
                id: acc.defaultPhoneNumberId,
                display: acc.defaultPhoneNumberDisplay,
                wabaId: acc.id || acc.wabaId,
              }
              break
            }
          }
          if (!found && user?.phoneNumberId) {
            for (const acc of normalized) {
              const ph = acc.phoneNumbers.find((p) => p.id === user.phoneNumberId)
              if (ph) {
                found = { id: user.phoneNumberId, display: ph.display_phone_number, wabaId: acc.id || acc.wabaId }
                break
              }
            }
          }
          newSummary.defaultPhoneNumber = found?.display || null
          newSummary.defaultPhoneNumberId = found?.id || null
          newSummary.defaultWabaId = found?.wabaId || null
        }

        setWabaAccounts(normalized)
        setSummary(newSummary)
        setError("")
        pickDefaultFromResult({ accounts: normalized })
        return result
      } else {
        console.error("[v0] Failed to fetch WABA accounts:", result.error)
        setError(result.error || "Failed to fetch WABA accounts")
        setWabaAccounts([])
        if (!result.error?.includes("credentials not configured") && !result.error?.includes("Please login")) {
          Swal.fire({ icon: "error", title: "Error", text: result.error || "Failed to fetch WABA accounts" })
        }
        return false
      }
    } catch (error) {
      console.error("[v0] Error fetching WABA accounts:", error)
      setError("Failed to fetch WABA accounts. Please check your connection and try again.")
      setWabaAccounts([])
      Swal.fire({
        icon: "error",
        title: "Connection Error",
        text: "Failed to fetch WABA accounts. Please check your connection and try again.",
      })
      return false
    } finally {
      setLoading(false)
    }
  }

  const handleAddWaba = async () => {
    if (!newWabaId.trim()) {
      setError("Please enter a valid WABA ID")
      Swal.fire({
        icon: "warning",
        title: "Invalid Input",
        text: "Please enter a valid WABA ID",
        confirmButtonColor: "#4A7C59",
      })
      return
    }

    try {
      setAddingWaba(true)
      setError("")

      console.log("[v0] Adding WABA ID:", newWabaId.trim())
      const result = await addWabaById(newWabaId.trim())
      console.log("[v0] Add WABA result:", result)

      if (result.success) {
        setNewWabaId("")
        setShowAddWaba(false)

        Swal.fire({
          icon: "success",
          title: "Success!",
          text: result.message || "WABA account added successfully!",
          confirmButtonColor: "#4A7C59",
        })

        fetchWabaAccounts() // Refresh the list
      } else {
        console.error("[v0] Failed to add WABA:", result.error)
        setError(result.error || "Failed to add WABA account")

        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.error || "Failed to add WABA account",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (error) {
      console.error("[v0] Error adding WABA:", error)
      setError("Failed to add WABA account. Please try again.")

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to add WABA account. Please try again.",
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setAddingWaba(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
      case "verified":
      case "connected":
      case "green":
        return "text-green-600 dark:text-green-400"
      case "pending":
      case "yellow":
        return "text-yellow-600 dark:text-yellow-400"
      case "rejected":
      case "disconnected":
      case "red":
        return "text-red-600 dark:text-red-400"
      default:
        return "text-gray-600 dark:text-gray-400"
    }
  }

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
      case "verified":
      case "connected":
      case "green":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
      case "pending":
      case "yellow":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
      case "rejected":
      case "disconnected":
      case "red":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
    }
  }

  const filteredAccounts = wabaAccounts.filter(
    (account) =>
      account.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.id?.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  if (selectedAccount && selectedPhoneNumber) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-gradient-to-br from-green-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800">
          <div className="mx-auto px-4 sm:px-6 lg:px-4 py-4 pt-20 lg:pt-4">
            {/* Back Button */}
            <div className="mb-6">
              <button
                onClick={handleBackToAccounts}
                className="inline-flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
              >
                <ArrowLeftIcon className="w-5 h-5 mr-2" />
                Back to WABA Accounts
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Sidebar - Phone Numbers */}
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Phone Numbers</h3>
                  <div className="space-y-3">
                    {selectedAccount.phoneNumbers?.map((phone) => (
                      <div
                        key={phone.id}
                        onClick={() => handlePhoneNumberClick(selectedAccount, phone)}
                        className={`p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
                          selectedPhoneNumber?.id === phone.id
                            ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                            : "border-gray-200 dark:border-gray-600 hover:border-green-300 dark:hover:border-green-600"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center flex-1">
                            <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-yellow-500 rounded-lg flex items-center justify-center mr-3">
                              <PhoneIcon className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-white">
                                {phone.display_phone_number}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {phone.verified_name || "Unnamed"}
                              </div>
                            </div>
                          </div>
                          <div className="text-right ml-2">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(phone.quality_rating)}`}
                            >
                              {phone.quality_rating || "Unknown"}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center">
                          {phone.default || selectedAccount.defaultPhoneNumberId === phone.id ? (
                            <span className="text-xs px-3 py-1 rounded-lg bg-green-600 text-white font-semibold flex items-center">
                              <CheckCircleIcon className="w-3 h-3 mr-1" />
                              Default
                            </span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleMakeDefaultPhone(
                                  selectedAccount.id || selectedAccount.wabaId,
                                  phone.id || phone.phoneNumberId,
                                )
                              }}
                              className="text-xs px-3 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 font-semibold transition-colors"
                            >
                              Make default
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Content - Phone Number Details */}
              <div className="lg:col-span-2">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                  {loadingPhoneDetails ? (
                    <div className="p-12 text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                      <p className="mt-4 text-gray-600 dark:text-gray-400">Loading phone number details...</p>
                    </div>
                  ) : phoneNumberDetails ? (
                    <div>
                      {/* Header */}
                      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-yellow-500 rounded-xl flex items-center justify-center mr-4">
                              <PhoneIcon className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {phoneNumberDetails.display_phone_number}
                              </h2>
                              <p className="text-gray-600 dark:text-gray-400">
                                {phoneNumberDetails.verified_name || "Infinity Solution Team"}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadge(phoneNumberDetails.status)}`}
                            >
                              {phoneNumberDetails.status || "Connected"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Tabs */}
                      <div className="border-b border-gray-200 dark:border-gray-700">
                        <nav className="flex space-x-8 px-6">
                          <button className="py-4 px-1 border-b-2 border-green-500 text-green-600 dark:text-green-400 font-medium text-sm">
                            Phone Number Details
                          </button>
                          <button className="py-4 px-1 border-b-2 border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 font-medium text-sm">
                            Two-step verification
                          </button>
                        </nav>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        {/* Phone Number Information */}
                        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Phone Number Details
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Phone Number
                              </label>
                              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                {phoneNumberDetails.display_phone_number}
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Country
                              </label>
                              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                {phoneNumberDetails.country?.flag} {phoneNumberDetails.country?.name}
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Verification Status
                              </label>
                              <span
                                className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                  phoneNumberDetails.code_verification_status === "VERIFIED"
                                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                    : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                }`}
                              >
                                {phoneNumberDetails.code_verification_status || "EXPIRED"}
                              </span>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Connection Status
                              </label>
                              <span
                                className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadge(phoneNumberDetails.status)}`}
                              >
                                {phoneNumberDetails.status}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Display name */}
                        <div className="mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Display name</h3>
                          <div className="flex items-center space-x-3">
                            <span className="text-gray-900 dark:text-white font-medium">
                              {phoneNumberDetails.verified_name}
                            </span>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(phoneNumberDetails.name_status)}`}
                            >
                              {phoneNumberDetails.name_status || "Approved"}
                            </span>
                          </div>
                        </div>

                        {/* Official business account */}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            Official business account
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400 text-sm">
                            An official business account gets a blue checkmark ✓ next to its name. This shows that
                            WhatsApp has confirmed that an authentic and notable brand owns this account.{" "}
                            <a href="#" className="text-blue-600 dark:text-blue-400 hover:underline">
                              Learn more.
                            </a>
                          </p>
                          <div className="flex items-center space-x-3 mb-2">
                            {phoneNumberDetails.is_official_business_account && (
                              <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                        </div>

                        {/* Quality Rating and Messaging Limits */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                            <div className="flex items-center mb-2">
                              <ShieldCheckIcon className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                              <h4 className="font-medium text-gray-900 dark:text-white">Quality Rating</h4>
                            </div>
                            <p className={`text-2xl font-bold ${getStatusColor(phoneNumberDetails.quality_rating)}`}>
                              {phoneNumberDetails.quality_rating}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Current messaging quality</p>
                          </div>

                          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center">
                                <BellIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
                                <h4 className="font-medium text-gray-900 dark:text-white">Messaging Limit</h4>
                              </div>
                              <button
                                type="button"
                                onClick={handleSyncWabaLimit}
                                disabled={syncingLimit}
                                className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-200 rounded-md transition-colors flex items-center gap-1"
                              >
                                <ArrowPathIcon className={`w-3 h-3 ${syncingLimit ? "animate-spin" : ""}`} />
                                Sync
                              </button>
                            </div>
                            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                              {phoneNumberDetails.messaging_limit_tier?.replace("TIER_", "") || user?.wabaLimit || "1K"}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Messages per 24 hours</p>
                          </div>

                          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                            <div className="flex items-center mb-2">
                              <PhoneIcon className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-2" />
                              <h4 className="font-medium text-gray-900 dark:text-white">Throughput</h4>
                            </div>
                            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                              {phoneNumberDetails.throughput?.level || "STANDARD"}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Message delivery speed</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-12 text-center">
                      <PhoneIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        Select a Phone Number
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        Choose a phone number from the left to view its details
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Main content */}
      <div className="">
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800">
          <div className="mx-auto px-4 sm:px-6 lg:px-4 py-4 pt-20 lg:pt-4">
            {/* WhatsApp Configuration Status */}
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 shadow-lg ${
                      isWhatsAppConfigured()
                        ? "bg-gradient-to-br from-green-500 to-green-600"
                        : "bg-gradient-to-br from-red-500 to-red-600"
                    }`}
                  >
                    {isWhatsAppConfigured() ? (
                      <CheckCircleIcon className="w-6 h-6 text-white" />
                    ) : (
                      <XCircleIcon className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">WhatsApp Configuration</h3>
                    <p
                      className={`text-sm ${
                        isWhatsAppConfigured() ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {getWhatsAppConfigurationStatus().message}
                    </p>
                    {getWhatsAppConfigurationStatus().details && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {getWhatsAppConfigurationStatus().details}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  {isWhatsAppConfigured() ? (
                    <div className="flex items-center text-green-600 dark:text-green-400">
                      <PhoneIcon className="w-5 h-5 mr-2" />
                      <span className="font-medium">Verified</span>
                    </div>
                  ) : (
                    <div>
                      {["superadmin", "admin"].includes(user?.role) ? (
                        <button
                          onClick={() => setShowConfigModal(true)}
                          className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
                        >
                          <CogIcon className="w-4 h-4 mr-2" />
                          Configure Credentials
                        </button>
                      ) : (
                        <div className="text-center">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">WhatsApp not configured</p>
                          <p className="text-xs text-red-600 dark:text-red-400">Contact support for configuration</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Header */}
            <div className="mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-yellow-600 bg-clip-text text-transparent mb-2">
                    WABA Dashboard
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 text-lg">
                    Welcome to your WhatsApp Business API dashboard
                  </p>
                </div>
                <div>
                  <button
                    type="button"
                    onClick={handleSyncWabaLimit}
                    disabled={syncingLimit}
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50"
                  >
                    <ArrowPathIcon className={`w-4 h-4 mr-2 ${syncingLimit ? "animate-spin" : ""}`} />
                    {syncingLimit ? "Syncing Limit..." : "Sync Limit from Meta"}
                  </button>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                    <BuildingOfficeIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Total WABA Accounts</h3>
                    <p className="text-3xl font-bold text-blue-600">{summary.totalWabaAccounts || "0"}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                    <CheckBadgeIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Verified Accounts</h3>
                    <p className="text-3xl font-bold text-green-600">{summary.verifiedAccounts || "0"}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                    <PhoneIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Total Phone Numbers</h3>
                    <p className="text-3xl font-bold text-purple-600">{summary.totalPhoneNumbers || "0"}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                    <SignalIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Connected Numbers</h3>
                    <p className="text-3xl font-bold text-orange-600">{summary.connectedNumbers || "0"}</p>
                  </div>
                </div>
              </div>
            </div>

            {summary.defaultPhoneNumber && (
              <div className="mb-8 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 p-6 rounded-2xl shadow-sm border border-green-200 dark:border-green-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                      <PhoneIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Default Phone Number</h3>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {summary.defaultPhoneNumber}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center text-green-600 dark:text-green-400">
                    <CheckCircleIcon className="w-6 h-6" />
                  </div>
                </div>
              </div>
            )}

            {/* Search and Add WABA */}
            <div className="mb-6 flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search WABA accounts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                />
              </div>

              <button
                onClick={handleSync}
                className="ml-3 inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-150"
                title="Sync WABA accounts"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 12a9 9 0 10-3 6.7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                  <path d="M21 3v6h-6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                </svg>
                Sync
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
                  <div>
                    <p className="text-red-800 dark:text-red-200 font-medium">API Request Failed</p>
                    <p className="text-red-700 dark:text-red-300 text-sm mt-1">{error}</p>
                    <div className="mt-2 text-xs text-red-600 dark:text-red-400">
                      <p>• Check if you're logged in with proper credentials</p>
                      <p>• Verify your WABA ID and access token permissions</p>
                      <p>• Ensure backend server is running on port 8004</p>
                      <p>• Set NEXT_PUBLIC_API_URL environment variable in Project Settings</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Add WABA Modal */}
            {showAddWaba && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md mx-4">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Add WABA Account</h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">WABA ID</label>
                    <input
                      type="text"
                      value={newWabaId}
                      onChange={(e) => setNewWabaId(e.target.value)}
                      placeholder="Enter WABA ID (e.g., 123456789012345)"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleAddWaba()
                        }
                      }}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Enter your WhatsApp Business Account ID from Meta Business Manager
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleAddWaba}
                      disabled={addingWaba}
                      className="flex-1 bg-gradient-to-r from-green-600 to-yellow-600 text-white font-semibold py-2 px-4 rounded-lg hover:from-green-700 hover:to-yellow-700 disabled:opacity-50 transition-all duration-200"
                    >
                      {addingWaba ? "Adding..." : "Add Account"}
                    </button>
                    <button
                      onClick={() => {
                        setShowAddWaba(false)
                        setNewWabaId("")
                        setError("")
                      }}
                      className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-all duration-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* WABA Accounts */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              {loading ? (
                <div className="py-12 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                  </div>
                  <p className="mt-4 text-gray-600 dark:text-gray-400">Loading WABA accounts...</p>
                </div>
              ) : filteredAccounts.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-yellow-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <PhoneIcon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No WABA Accounts Found</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                    {!isWhatsAppConfigured()
                      ? "Configure your WhatsApp credentials first to access WABA accounts."
                      : "You don't have any WhatsApp Business API accounts connected. Add your first WABA account to get started."}
                  </p>
                  <div className="space-y-4">
                    {isWhatsAppConfigured() ? (
                      <button
                        onClick={() => setShowAddWaba(true)}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-yellow-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-yellow-700 transition-all duration-200 shadow-lg"
                      >
                        <PlusIcon className="w-5 h-5 mr-2" />
                        Add WABA Account
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowConfigModal(true)}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg"
                      >
                        <CogIcon className="w-5 h-5 mr-2" />
                        Configure WhatsApp
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Account</th>
                        <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Status</th>
                        <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">
                          Phone Numbers
                        </th>
                        <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Currency</th>
                        <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Type</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredAccounts.map((account) => (
                        <tr
                          key={account.id}
                          onClick={() => handlePhoneNumberClick(account, account.phoneNumbers?.[0])}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200 cursor-pointer"
                        >
                          <td className="py-4 px-6">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-yellow-500 rounded-xl flex items-center justify-center mr-3 shadow-lg">
                                <PhoneIcon className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900 dark:text-white">
                                  {account.name || "Unnamed Account"}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">ID: {account.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="space-y-1">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(account.account_review_status)}`}
                              >
                                {account.account_review_status || "Unknown"}
                              </span>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Business: {account.business_verification_status || "Unknown"}
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center">
                              <PhoneIcon className="w-4 h-4 text-gray-400 mr-1" />
                              <span className="font-medium text-gray-900 dark:text-white">
                                {account.phoneNumberCount || account.phoneNumbers?.length || 0}
                                {account.defaultPhoneNumberDisplay
                                  ? ` • ${account.defaultPhoneNumberDisplay}`
                                  : account.phoneNumbers?.[0]
                                    ? ` • ${account.phoneNumbers[0].display_phone_number}`
                                    : ""}
                              </span>
                              {account.phoneNumbers && account.phoneNumbers.length > 0 && (
                                <div className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                  {account.phoneNumbers[0].display_phone_number}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className="text-gray-900 dark:text-white font-medium">
                              {account.currency || "USD"}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                account.type === "owned"
                                  ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                  : account.type === "pinacle"
                                    ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                                    : "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                              }`}
                            >
                              {account.type || "Unknown"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-lg mx-4">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Configure WhatsApp Credentials</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  WABA ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={credentials.wabaId}
                  onChange={(e) => setCredentials({ ...credentials, wabaId: e.target.value })}
                  placeholder={user?.wabaId ? `Current: ${user.wabaId}` : "Enter WABA ID"}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {user?.wabaId && <p className="text-xs text-gray-500 mt-1">Current: {user.wabaId}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number ID
                </label>
                <input
                  type="text"
                  value={credentials.phoneNumberId}
                  onChange={(e) => setCredentials({ ...credentials, phoneNumberId: e.target.value })}
                  placeholder={user?.phoneNumberId ? `Current: ${user.phoneNumberId}` : "Enter Phone Number ID"}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {user?.phoneNumberId && <p className="text-xs text-gray-500 mt-1">Current: {user.phoneNumberId}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Business Account ID
                </label>
                <input
                  type="text"
                  value={credentials.businessAccountId}
                  onChange={(e) => setCredentials({ ...credentials, businessAccountId: e.target.value })}
                  placeholder={
                    user?.businessAccountId ? `Current: ${user.businessAccountId}` : "Enter Business Account ID"
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {user?.businessAccountId && (
                  <p className="text-xs text-gray-500 mt-1">Current: {user.businessAccountId}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Access Token / API Key <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={credentials.token}
                  onChange={(e) => setCredentials({ ...credentials, token: e.target.value })}
                  placeholder={
                    user?.token || user?.wptoken
                      ? "Token is configured (enter new token to update)"
                      : "Enter Access Token or API Key"
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {(user?.token || user?.wptoken) && (
                  <p className="text-xs text-green-600 mt-1">✓ Access token is configured</p>
                )}
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  💡 <strong>Required:</strong> WABA ID and Access Token for full functionality. You can update
                  individual fields - leave empty to keep current values.
                  {user?.operator === "pinacle" && (
                    <span className="block mt-1">
                      <strong>Pinacle Operator:</strong> Use your Pinbot API key as the Access Token.
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleConfigureCredentials}
                disabled={savingCredentials}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-2 px-4 rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 transition-all duration-200"
              >
                {savingCredentials ? "Updating..." : isWhatsAppConfigured() ? "Update Credentials" : "Save Credentials"}
              </button>
              <button
                onClick={() => {
                  setShowConfigModal(false)
                  loadExistingCredentials()
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
